// vim: set ts=2 sts=2 sw=2 et:

import React from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import type { GlobalState } from "../../../types/stateTypes";

import { LAB_SCENARIOS, LAB_TOURNAMENT, LabScenario } from "../labScenarios";
import { LAB_SPONSORS } from "../labSponsors";
import LabStage, { LAB_BACKDROPS, LabBackdrop, parseLabZoom, useLabBody } from "../LabStage";
import BroadcastOverlayView from "../../streaming/broadcast/BroadcastOverlayView";
import OverlayLuces from "./OverlayLuces";
import OverlayPlaca from "./OverlayPlaca";
import { buildAthleteView, OverlayView } from "../../streaming/broadcast/overlayView";

import styles from "../Lab.module.scss";

type Design = "fusion" | "placa" | "luces";

// La fusión es la dirección elegida; A y B quedan como referencia mientras se itera la estética.
const DESIGNS: ReadonlyArray<{ id: Design; label: string }> = [
  { id: "fusion", label: "Fusión (header B + footer A)" },
  { id: "placa", label: "A · Placa (referencia)" },
  { id: "luces", label: "B · Luces (referencia)" },
];

const DEMO_STEP_MS = 5000;

const pick = <T extends string>(value: string | null, options: ReadonlyArray<{ id: T }>, fallback: T): T =>
  options.some((option) => option.id === value) ? (value as T) : fallback;

const buildView = (scenario: LabScenario, clockSeconds: number, tournamentName: string): OverlayView => ({
  tournamentName,
  sessionLabel: `Día ${LAB_TOURNAMENT.day}, tarima ${LAB_TOURNAMENT.platformId}`,
  athlete: scenario.attempt === null ? null : buildAthleteView(scenario.attempt, scenario.reveal),
  waitingForNext: scenario.waitingForNext === true,
  lights: scenario.judgeLights,
  result: scenario.reveal,
  clockSeconds: scenario.clockSeconds === null ? 60 : clockSeconds,
  clockRunning: scenario.clockSeconds !== null && scenario.reveal === null,
});

const OverlayLabRoute = () => {
  useLabBody();
  const location = useLocation();
  // Como en producción, el nombre sale de la configuración del meet; sin meet cargado se usa el de ejemplo.
  const meetName = useSelector((state: GlobalState) => state.meet.name.trim());
  const initial = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [design, setDesign] = React.useState<Design>(() => pick(initial.get("diseno"), DESIGNS, "fusion"));
  const [scenarioId, setScenarioId] = React.useState(() => pick(initial.get("escena"), LAB_SCENARIOS, LAB_SCENARIOS[0].id));
  const [backdrop, setBackdrop] = React.useState<LabBackdrop>(() => pick(initial.get("fondo"), LAB_BACKDROPS, "textura"));
  const [controlsVisible, setControlsVisible] = React.useState(initial.get("controles") !== "0");
  const [demo, setDemo] = React.useState(initial.get("demo") === "1");
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);

  const scenarioIndex = Math.max(
    0,
    LAB_SCENARIOS.findIndex((scenario) => scenario.id === scenarioId),
  );
  const scenario = LAB_SCENARIOS[scenarioIndex];
  const [clockSeconds, setClockSeconds] = React.useState(scenario.clockSeconds ?? 60);

  const step = React.useCallback((delta: number) => {
    setScenarioId((current) => {
      const index = LAB_SCENARIOS.findIndex((candidate) => candidate.id === current);
      return LAB_SCENARIOS[(index + delta + LAB_SCENARIOS.length) % LAB_SCENARIOS.length].id;
    });
  }, []);

  // La URL refleja la selección para poder copiarla a OBS o compartirla.
  React.useEffect(() => {
    const params = new URLSearchParams({ diseno: design, escena: scenarioId, fondo: backdrop });
    if (!controlsVisible) params.set("controles", "0");
    if (demo) params.set("demo", "1");
    const zoom = initial.get("zoom");
    if (zoom !== null) params.set("zoom", zoom);
    window.history.replaceState(null, "", `${location.pathname}?${params.toString()}`);
  }, [backdrop, controlsVisible, demo, design, initial, location.pathname, scenarioId]);

  React.useEffect(() => {
    setClockSeconds(scenario.clockSeconds ?? 60);
    if (scenario.clockSeconds === null || scenario.reveal !== null) return undefined;
    const timer = window.setInterval(() => setClockSeconds((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [scenario]);

  React.useEffect(() => {
    if (!demo) return undefined;
    const timer = window.setInterval(() => step(1), DEMO_STEP_MS);
    return () => window.clearInterval(timer);
  }, [demo, step]);

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLSelectElement || event.target instanceof HTMLInputElement) return;
      if (event.key === "h" || event.key === "H") setControlsVisible((visible) => !visible);
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
      const byNumber = DESIGNS[Number(event.key) - 1];
      if (byNumber !== undefined) setDesign(byNumber.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  React.useEffect(() => () => {
    if (imageUrl !== null) URL.revokeObjectURL(imageUrl);
  }, [imageUrl]);

  const onImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    setImageUrl(URL.createObjectURL(file));
    setBackdrop("imagen");
  };

  const view = buildView(scenario, clockSeconds, meetName || LAB_TOURNAMENT.name);
  const Design = design === "luces" ? OverlayLuces : design === "placa" ? OverlayPlaca : BroadcastOverlayView;

  return (
    <>
      <LabStage backdrop={backdrop} imageUrl={imageUrl} zoom={parseLabZoom(initial.get("zoom"))}>
        <Design view={view} sponsors={LAB_SPONSORS} />
      </LabStage>

      {controlsVisible ? (
        <aside className={styles.controls} aria-label="Controles del lab">
          <div className={styles.controlsTitle}>
            <h1>Overlay de transmisión</h1>
            <Link to="/lab">Lab</Link>
          </div>
          <label>
            Diseño
            <select value={design} onChange={(event) => setDesign(event.target.value as Design)}>
              {DESIGNS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Escena
            <select value={scenario.id} onChange={(event) => setScenarioId(event.target.value)}>
              {LAB_SCENARIOS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Fondo
            <select value={backdrop} onChange={(event) => setBackdrop(event.target.value as LabBackdrop)}>
              {LAB_BACKDROPS.map((option) => (
                <option key={option.id} value={option.id} disabled={option.id === "imagen" && imageUrl === null}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Usar una captura del stream como fondo
            <input type="file" accept="image/*" onChange={onImage} />
          </label>
          <div className={styles.controlsRow}>
            <button type="button" onClick={() => step(-1)}>
              Anterior
            </button>
            <button type="button" aria-pressed={demo} onClick={() => setDemo((value) => !value)}>
              {demo ? "Detener" : "Reproducir"}
            </button>
            <button type="button" onClick={() => step(1)}>
              Siguiente
            </button>
          </div>
          <p className={styles.controlsHint}>
            H oculta estos controles. ← → cambian de escena y 1, 2 y 3 de diseño. El torneo sale de la configuración del meet. La URL guarda la selección: con
            controles=0 queda lista para OBS.
          </p>
        </aside>
      ) : null}
    </>
  );
};

export default OverlayLabRoute;
